import { fromEvent, filter, tap } from "rxjs";
import { store } from '@redux/store';
import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import uiStateSlice from 'layout/redux/uiStateSlice';
import { LocationType, searchParametersActions } from 'features/searchResultsFacets';
import GoogleAnalyticsCustomDimensions from "constants/googleAnalyticsCustomDimension";

const listViewContainer = document.querySelector('[data-list-view-element]');

// create a stream of click events on the listViewContainer element
const containerClick$ = fromEvent(listViewContainer, 'click');

const isBrandCard = (event) => !!event.target.closest('[data-builder-brand-card]');
const isBasicBrandCard = (event) => !!event.target.closest('[data-basic-card]');
const doesCardApplyBrandFilter = (event) => !!event.target.closest('[data-apply-brand-filter]');
const doesCardPointToReviewsBuilderShowcasePage = (event) => !!event.target.closest('[data-reviews-bsp]');
const isViewHomesLinkClick = (event) => {
    const anchorLink = event.target.closest('[data-brand-view-homes-link]');
    return !!anchorLink;
};
const isViewCommunitiesLinkClick = (event) => {
  const anchorLink = event.target.closest('[data-brand-view-communities-link]');
  return !!anchorLink;
};
const isBrandNameLinkClick = (event) => {
  const anchorLink = event.target.closest('a[data-builder-brand-name-about-url]') ||
   event.target.closest('a[data-builder-brand-name-reviews-url]');
  return !!anchorLink;
};
const isBrandLogoLinkClick = (event) => {
    const anchorLink = event.target.closest('a[data-brand-logo-link]') || 
    event.target.closest('a[data-brand-logo]');
    return !!anchorLink;
};

const hasAboutPage = (event) => {
  const anchorLink = event.target.closest('[data-has-about-page]');
  return !!anchorLink;
};
const isLearnMoreLinkClick = (event) => {
  const anchorLink = event.target.closest('a[data-brand-learn-more-link]');
  return !!anchorLink;
};

/**
 * Function to add GA Events and Custom Dimensions.
 */

function addCustomDimensions (event){
  const card = event.target.closest('[data-builder-brand-card]');
  const brandName = card.dataset.brandName;

  const customDimensions = {
    [GoogleAnalyticsCustomDimensions.BrandName().getName()]: brandName,
  };
  return customDimensions;
};  

/**
 * This function build the Google Analytic object to set the event tracking.
 */
const emitGoogleAnalyticsEvent = (category, action, label, dimensions) => {
  const googleAnalyticsDTO = new GoogleAnalyticsEvent(category, action, label, dimensions);
  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
}

// Set the GA Events in the "Builder Logo" link.
containerClick$.pipe(
  filter(isBrandCard),
  filter(event => isBrandLogoLinkClick(event)),
).subscribe( (event) => { 
  emitGoogleAnalyticsEvent(
    'Search Events', 
    'Search Refinement', 
    'All Builders Logo Clicked', 
    addCustomDimensions(event)); 
  }
);

// Set the GA Events on the basic card.
containerClick$.pipe(
  filter(isBasicBrandCard),
).subscribe( (event) => { 
  emitGoogleAnalyticsEvent(
    'Search Events', 
    'Search Refinement', 
    'Basic Builders Card Clicked', 
    addCustomDimensions(event)); 
  }
);

// Set the GA Events in the "Learn More" link.
containerClick$.pipe(
  filter(isBasicBrandCard),
  filter(event => isLearnMoreLinkClick(event))
).subscribe( () => { emitGoogleAnalyticsEvent('Search Events', 'Search Refinement',
 'Basic Builders Learn More Clicked'); });

// filter the stream to only include click events that are made on the card.
// also filters the cards that shouldn't apply the custom click, 
const cardClick$ = containerClick$.pipe(
  filter(isBrandCard),
  filter(event => !isViewHomesLinkClick(event)),
  filter(event => !isBrandNameLinkClick(event)),
  filter(event => !isBrandLogoLinkClick(event)),
  filter(event => !hasAboutPage(event)),
  filter(doesCardApplyBrandFilter),
  tap((event) => {
    event.preventDefault();
  })
);

const reviewsCardClick$ = containerClick$.pipe(
    filter(isBrandCard),
    filter(event => !isViewHomesLinkClick(event)),
    filter(event => !isViewCommunitiesLinkClick(event)),
    filter(event => !isBrandLogoLinkClick(event)),
    filter(event => !isBrandNameLinkClick(event)),
    filter(doesCardPointToReviewsBuilderShowcasePage),
  );

containerClick$.pipe(
  filter(isBrandCard),
  filter(event => isBrandNameLinkClick(event))
).subscribe((event) => {
  emitGoogleAnalyticsEvent(
    'Search Events',
    'Search Refinement',
    'All Builders Name Clicked',
    addCustomDimensions(event));
}
);

const viewHomesClick$ = containerClick$.pipe(
    filter(isBrandCard),
    filter(isViewHomesLinkClick),
    tap((event) => event.preventDefault())
);

const viewCommunitiesClick$ = containerClick$.pipe(
  filter(isBrandCard),
  filter(isViewCommunitiesLinkClick),
  tap((event) => event.preventDefault())
);

const emitFetchBrandAction = (event) => {
    const card = event.target.closest('[data-builder-brand-card]');
    const resultType = event.target.dataset.resultTypeRedirect || card.dataset.resultTypeRedirect;
    const productType = event.target.dataset.productTypeRedirect ||  card.dataset.productTypeRedirect;
    const locationType = event.target.dataset.locationTypeRedirect ||  card.dataset.locationTypeRedirect;
    const brand = { name: card.dataset.brandName, id: card.dataset.brandId }
    const searchParametersState = store?.getState()?.searchParameters;

    // if the card is a no results card, all of their links are going to point to a market search by design
    if (searchParametersState.location.locationType !== LocationType.RectangularArea.name &&
      !!card.dataset.suggestedResultsCard
    ) {
      store.dispatch(searchParametersActions.fetchViewBrandMarketResults({
        brand, resultType, productType, locationType
      }));

      return;
    }

    store.dispatch(searchParametersActions.fetchViewBrandResults({
      brand, resultType, productType
    }));
}

const redirectClickEventToUrl = (url) => {
    window.open(url, '_blank');
  }

containerClick$.pipe(
    filter(isBrandCard),
    filter(event => !isViewHomesLinkClick(event)),
    filter(event => !isViewCommunitiesLinkClick(event)),
    filter(event => !isBrandNameLinkClick(event)),
    filter(event => !isBrandLogoLinkClick(event)),
    filter(event => !doesCardPointToReviewsBuilderShowcasePage(event)),
    filter(event => hasAboutPage(event)),
  ).subscribe((event) => {
    const brandCardElement = event.target.closest('[data-paid-builder-brand-card]');
    const url = brandCardElement?.querySelector('a[data-brand-logo-link]')?.href;
    redirectClickEventToUrl(url);
    emitGoogleAnalyticsEvent('Search Events', 'Search Refinement', 'All Builders Clicked');
  });


// subscribe to the cardClick$ stream and execute a dispatch to update the brand.
cardClick$.subscribe((event) => {
  emitFetchBrandAction(event)
});

// subscribe to the cardClick$ stream and execute a dispatch to update the brand.
reviewsCardClick$.subscribe((event) => {
    const brandCardElement = event.target.closest('[data-builder-brand-card]');
    const url = brandCardElement?.querySelector('a[data-builder-brand-name-reviews-url]')?.href;
    redirectClickEventToUrl(url);
    emitGoogleAnalyticsEvent('Search Events', 'Search Refinement', 'All Builders Reviews Clicked');
  });

// subscribe to the viewHomesClick$ stream and execute a dispatch to update the brand.
viewHomesClick$.subscribe((event) => {
    emitFetchBrandAction(event);
    emitGoogleAnalyticsEvent('Search Events', 'Search Refinement', 'Community & Homes Tab', addCustomDimensions(event));
});

// subscribe to the viewCommunitiesClick$ stream and execute a dispatch to update the brand.
viewCommunitiesClick$.subscribe((event) => {
  emitFetchBrandAction(event);
  emitGoogleAnalyticsEvent('Search Events', 'Search Refinement', 'Community & Homes Tab', addCustomDimensions(event));
});